import { Component, Element, h, Prop } from "@stencil/core"
import {
  getAppearance,
  getIconLinkClassNames,
  hasSlot,
  isTouchscreen,
  translate,
} from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-teaser",
  styleUrl: "teaser.scss",
  shadow: true,
})
export class Teaser {
  @Element() public el!: HTMLSdxTeaserElement

  /**
   * The layout type defines the appearance of the teaser.
   */
  @Prop({ reflect: true }) public layout:
    | "card"
    | "split"
    | "split-sidekick"
    | "cover"
    | "cover-sidekick"
    | "gradient-sidekick"
    | "product"
    | "product-out-of-stock"
    | "product-button"
    | "standard" = "card"

  /**
   * Background gradient colours for `gradient-sidekick` and `standard` layouts.
   * Important: Gradient-3 background is reserved for B2B applications only and may not be used by other departments.
   */
  @Prop() public background: "gradient-1" | "gradient-2" | "gradient-3" | "" =
    ""

  /**
   * The content area position for `standard` layout.
   */
  @Prop() public contentPosition: "left" | "bottom" | "top" = "left"

  /**
   * Background colours for `standard` and `split` layouts.
   */
  @Prop() public backgroundColor: "horizon" | "cloud" | "white" | "" = ""

  /**
   * Shows a border. Used when `background-color="white"`.
   */
  @Prop() public border: boolean = false

  /**
   * The colour variation of tags and counters.
   */
  @Prop() public theme?: HTMLSdxTagElement["theme"]

  /**
   * Title that will be displayed in the teaser's header.
   */
  @Prop() public label?: string

  /**
   * What aria level the label in the header relates to for accessibility.
   */
  @Prop() public labelAriaLevel?: number

  /**
   * The visual heading size (H2 or H4) of the label. Only for `standard` layout.
   */
  @Prop() public labelVisualLevel?: 2 | 4

  /**
   * Optional tag label.
   */
  @Prop() public tagLabel?: HTMLSdxTagElement["label"]

  /**
   * Optional tag theme. Requires a `tag-label`.
   * @deprecated use `theme` instead.
   */
  @Prop() public tagTheme: HTMLSdxTagElement["theme"] = "standard"

  /**
   * Optional tag icon. Requires a `tag-label`.
   */
  @Prop() public tagIconName: HTMLSdxTagElement["iconName"]

  /**
   * Optional button icon. Useful for `product-button` layout.
   */
  @Prop() public buttonIconName: HTMLSdxButtonElement["iconName"]

  /**
   * Optional date for time counter, useful for product layouts.
   */
  @Prop() public date: HTMLSdxCountdownElement["date"]

  /**
   * Optional amount for stock counter, useful for product layouts.
   */
  @Prop() public stockAmount: HTMLSdxStockCounterElement["amount"] = 0

  /**
   * Optional total for stock counter, useful for product layouts.
   */
  @Prop() public stockTotal: HTMLSdxStockCounterElement["total"] = 0

  /**
   * The image to display on the teaser (also supports SVG).
   */
  @Prop() public imageSrc: string = ""

  /**
   * How to handle the loading of the image if outside the visual viewport (reflects the `loading` property, see
   * [mdn web docs](//developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/loading)).
   */
  @Prop() public imageLoading: "lazy" | "eager" = "lazy"

  /**
   * How to prioritize the fetch of the image relative to others (reflects the `fetchPriority` property, see
   * [mdn web docs](//developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/fetchPriority)).
   */
  @Prop() public imageFetchPriority: "high" | "low" | "auto" = "auto"

  /**
   * The image’s aspect ratio. Only for `standard` layout.
   */
  @Prop() public imageAspectRatio: "4:3" | "16:9" | "1:1" = "4:3"

  /**
   * An alternate text for the image if it can not be displayed and for screen
   * readers.
   */
  @Prop() public imageAlt: string = ""

  /**
   * How the image fits inside (reflects the CSS `"background-size"` property, see
   * [mdn web docs](//developer.mozilla.org/en-US/docs/Web/CSS/background-size)).
   * Only for `cover`/`cover-sidekick` and `standard` teasers.
   */
  @Prop() public backgroundSize: string = "cover"

  /**
   * Optional background image. Only for `split`/`split-sidekick` and `standard` teasers.
   */
  @Prop() public backgroundImageSrc?: string

  /**
   * How the image fits inside (reflects the CSS `"object-fit"` property, see
   * [mdn web docs](//developer.mozilla.org/en-US/docs/Web/CSS/object-fit)).
   * Only for `split`/`split-sidekick` and `standard` teasers.
   */
  @Prop() public objectFit?:
    | "contain"
    | "cover"
    | "fill"
    | "none"
    | "scale-down"

  /**
   * Renders a standalone or anchor link at the bottom of the teaser. Needs the
   * `href-label` attribute.
   */
  @Prop() public href?: string

  /**
   * Renders a standalone or anchor link at the bottom of the teaser. Needs the
   * `href` attribute.
   */
  @Prop() public hrefLabel?: string = " "

  /**
   * Adds a meaningful description to the link for screen readers.
   */
  @Prop() public hrefAriaLabel?: string

  /**
   * Standalone link "target" (`"_blank"`, `"_parent"`, `"_top"`, `"_self"`, or
   * the name of a window or frame). Needs the `href` attribute.
   */
  @Prop() public target?: string

  /**
   * If the browser should automatically insert hyphens where appropriate.
   */
  @Prop() public hyphens: "auto" | "manual" = "manual"

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated: boolean = true

  private isLayout(
    group: "split" | "cover" | "gradient" | "product" | "standard",
  ) {
    const groups: {
      split: HTMLSdxTeaserElement["layout"][]
      cover: HTMLSdxTeaserElement["layout"][]
      gradient: HTMLSdxTeaserElement["layout"][]
      product: HTMLSdxTeaserElement["layout"][]
      standard: HTMLSdxTeaserElement["layout"][]
    } = {
      split: ["split", "split-sidekick"],
      cover: ["cover", "cover-sidekick"],
      gradient: ["gradient-sidekick"],
      product: ["product", "product-out-of-stock", "product-button"],
      standard: ["standard"],
    }
    return groups[group].includes(this.layout)
  }

  private hasDarkBackground() {
    return (
      this.isLayout("cover") ||
      this.isLayout("gradient") ||
      (this.isLayout("split") && !!this.backgroundImageSrc) ||
      (this.isLayout("standard") && !!this.backgroundImageSrc) ||
      (this.isLayout("standard") && !!this.background)
    )
  }

  private getHeading() {
    let h = "h3"

    if (this.isLayout("standard") && !!this.labelVisualLevel) {
      h = `h${this.labelVisualLevel}`
    } else if (
      this.isLayout("cover") ||
      this.isLayout("split") ||
      this.isLayout("gradient") ||
      this.isLayout("standard")
    ) {
      h = "h2"
    } else if (this.isLayout("product")) {
      h = "h4"
    }

    return h
  }

  private hasContainedImage() {
    return (
      !!this.imageSrc && this.objectFit !== "cover" && this.objectFit !== "fill"
    )
  }

  private getComponentClassNames() {
    return {
      component: true,
      [this.layout]: true,
      [getAppearance(this.el)]: true,
      [this.background]: true,
      [this.hyphens]: true,
      "is-touchscreen": isTouchscreen(),
      "has-dark-background": this.hasDarkBackground(),
      "has-image": !!this.imageSrc,
      "has-contained-image": this.hasContainedImage(),
      "has-bg-image": !!this.backgroundImageSrc,
      [`content-${this.contentPosition}`]: this.isLayout("standard"),
      [this.backgroundColor]:
        this.isLayout("standard") || this.isLayout("split"),
      animated: this.animated,
      border: this.border,
    }
  }

  private getLinkClassNames() {
    const { target, href } = this

    return {
      [getIconLinkClassNames(href, target)]: true,
      "link--white": this.hasDarkBackground(),
      animated: this.animated,
    }
  }

  private getLabelClassNames() {
    return {
      label: true,
      [this.getHeading()]: true,
    }
  }

  private getImageClassNames() {
    return {
      image: true,
      "image--rounded": true,
      [`ratio-${this.imageAspectRatio.replace(/:/, "-")}`]:
        this.isLayout("standard"),
    }
  }

  private getBackgroundStyles() {
    if (
      !this.isLayout("cover") &&
      !(this.isLayout("split") && this.backgroundImageSrc) &&
      !(this.isLayout("standard") && this.backgroundImageSrc)
    ) {
      return
    }

    const gradient =
      // $color-dusk-tint-2-dark-theme
      "linear-gradient(var(--gradient-direction, 90deg), #1d1d1d 0%, transparent var(--gradient-width, 55%))"
    return {
      backgroundImage: `${gradient}, url(${
        this.isLayout("split") || this.isLayout("standard")
          ? this.backgroundImageSrc
          : this.imageSrc
      })`,
      backgroundSize: `${this.backgroundSize}, ${this.backgroundSize}`,
    }
  }

  public render() {
    return (
      <a
        class={this.getComponentClassNames()}
        style={this.getBackgroundStyles()}
        href={this.href}
        target={this.target}
        draggable={false}
      >
        <div class="first">
          <img
            class={this.getImageClassNames()}
            src={this.imageSrc}
            alt={this.imageAlt}
            draggable={false}
            style={
              (this.isLayout("split") || this.isLayout("standard")) &&
              this.objectFit
                ? { objectFit: this.objectFit }
                : undefined
            }
            loading={this.imageLoading}
            ref={(img) => (img!.fetchPriority = this.imageFetchPriority)}
          />
        </div>
        <div class="second">
          {!this.date && !this.stockAmount && this.tagLabel && (
            <sdx-tag
              class="tag"
              label={this.tagLabel}
              theme={this.theme || this.tagTheme}
              icon-name={this.tagIconName}
              background={this.hasDarkBackground() ? "dark" : "light"}
            />
          )}
          {this.date && (
            <sdx-countdown class="time-counter" size="small" date={this.date} />
          )}
          {!!this.stockAmount && (
            <sdx-stock-counter
              class="stock-counter"
              theme={this.theme}
              amount={this.stockAmount}
              total={this.stockTotal}
            />
          )}
          {this.isLayout("product") &&
            !this.date &&
            !this.stockAmount &&
            !this.tagLabel && <div class="reserved-space" />}

          {this.label && (
            <div
              class={this.getLabelClassNames()}
              role={this.labelAriaLevel ? "heading" : undefined}
              aria-level={this.labelAriaLevel}
            >
              {this.label}
            </div>
          )}

          <div class={hasSlot(this.el) ? "description" : "empty description"}>
            <slot />
          </div>

          {this.layout === "product-button" && (
            <button class="button button--secondary">
              {!!this.buttonIconName && (
                <sdx-icon icon-name={this.buttonIconName} />
              )}
              {this.hrefLabel || translate("Add to cart", this.el.lang)}
            </button>
          )}

          {this.layout !== "product-button" && (
            <a
              href={this.href}
              class={this.getLinkClassNames()}
              aria-label={this.hrefAriaLabel}
              target={this.target}
            >
              {this.hrefLabel}
            </a>
          )}
        </div>
      </a>
    )
  }
}
